import {
  Button,
  Dot,
  Drawer,
  Icon,
  Pagination,
  SearchBox,
  Spinner,
} from "components";
import { config } from "constant";
import { useAxios } from "hooks";
import { PersonDetailsContext } from "pages/PersonDetails";
import { Dispatch, useContext, useEffect, useMemo, useState } from "react";
import { Paged, Room, ToggleProps } from "types";

type Props = ToggleProps & {
  activeRoom: Room.Item | null;
  onSelect: Dispatch<Room.Item | null>;
};

export default function SelectRoomDrawer({
  isOpen,
  toggle,
  activeRoom,
  onSelect,
}: Props) {
  const [axios, loading] = useAxios();
  const { user } = useContext(PersonDetailsContext);
  const [rooms, setRooms] = useState<Paged<Room.Item> | null>(null);
  const [params, setParams] = useState<Room.Params>({
    pageSize: config.pageSize,
    pageNumber: 1,
    IsPublic: true,
  });
  const hasRooms = !!rooms?.items.length;
  const isAllActive = !activeRoom;
  const allLinksCount = useMemo(() => {
    const count = rooms?.items.reduce((a, b) => a + b.linkCount, 0) ?? 0;
    if (rooms?.totalPages === params.pageNumber) return `${count}`;
    return `+${count}`;
  }, [params.pageNumber, rooms?.items, rooms?.totalPages]);
  const handleSetParams = (key: keyof Room.Params) => {
    return (value: any) => {
      const isPageNumber = key === "pageNumber";
      setParams((p) => ({
        ...p,
        [key]: value,
        ...(!isPageNumber && { pageNumber: 1 }),
      }));
    };
  };
  const handleSelect = (e: Room.Item | null) => {
    return () => {
      onSelect(e);
      toggle?.();
    };
  };
  const getRooms = () => {
    if (!isOpen) return;
    const url = `/People/${user.id}/rooms`;
    const config = { params };
    const isFirstPage = config.params.pageNumber === 1;
    isFirstPage && setRooms(null);
    axios.get(url, config).then(({ data }) => {
      if (isFirstPage) return setRooms(data);
      setRooms((p) => ({ ...p!, items: [...p?.items!, ...data.items] }));
    });
  };

  useEffect(getRooms, [params, user.id, isOpen]);
  return (
    <Drawer isOpen={isOpen} toggle={toggle}>
      <Drawer.Menu>
        <Drawer.Header className="flex items-center gap-2 py-4">
          <h4 className="flex-1 text-base text-[#353740] font-medium text-start">
            Select Room
          </h4>
          <Button
            type="button"
            variant="text"
            color="danger"
            className="text-sm font-medium -me-3"
            onClick={toggle}
          >
            Cancel
          </Button>
        </Drawer.Header>
        <Drawer.Header className="py-2.5">
          <SearchBox
            value={params.filter}
            onSubmit={handleSetParams("filter")}
          />
        </Drawer.Header>
        <Drawer.Body>
          {hasRooms && (
            <ul className="w-full">
              <li
                className="flex items-center cursor-pointer gap-2.5 py-4"
                onClick={handleSelect(null)}
              >
                <div className="size-12 flex justify-center items-center rounded bg-default-theme">
                  <Icon
                    name="LinkCircle"
                    variant={isAllActive ? "Bold" : "Linear"}
                    className="size-6 text-[#353740]"
                  />
                </div>
                <div className="flex-1 space-y-1">
                  <h3 className="block truncate text-base text-[#353740] font-medium">
                    All Link
                  </h3>
                  <p className="block truncate text-sm text-[#5E6278] font-medium">
                    {allLinksCount} Links
                  </p>
                </div>
              </li>
              <li>
                <fieldset className="border border-[#ECEFF2] border-x-0 border-b-0">
                  <legend className="block w-fit mx-auto text-[0.625rem] font-medium text-[#9A9EB1] px-4">
                    Or select Rooms
                  </legend>
                </fieldset>
              </li>
              {rooms?.items.map((e) => {
                const active = e.id === activeRoom?.id;
                return (
                  <li
                    key={e.id}
                    className="flex items-center gap-2.5 py-4 cursor-pointer border-b border-[#ECEFF2]"
                    onClick={handleSelect(e)}
                  >
                    <div className="size-12 flex justify-center items-center rounded bg-default-theme">
                      <Icon
                        name="People"
                        variant={active ? "Bold" : "Linear"}
                        className="size-6 text-[#353740]"
                      />
                    </div>
                    <div className="flex-1 space-y-1">
                      <h3 className="block truncate text-base text-[#353740] font-medium">
                        {e.title}
                      </h3>
                      <p className="block truncate text-sm text-[#5E6278] font-medium">
                        {e.linkCount} Links
                      </p>
                    </div>
                    {active && <Dot />}
                  </li>
                );
              })}
            </ul>
          )}
          {loading ? (
            <div className="flex justify-center">
              <Spinner />
            </div>
          ) : (
            <Pagination
              activePage={params.pageNumber}
              setActivePage={handleSetParams("pageNumber")}
              totalPages={rooms?.totalPages}
            />
          )}
        </Drawer.Body>
      </Drawer.Menu>
    </Drawer>
  );
}
